import React from 'react';
import { string } from 'prop-types';
import Decimal from 'decimal.js';

import { FormattedMessage, intlShape } from '../../util/reactIntl';
import { formatMoney } from '../../util/currency';
import { types as sdkTypes } from '../../util/sdkLoader';
import {
  propTypes,
  LINE_ITEM_CUSTOMER_COMMISSION,
  LINE_ITEM_PROVIDER_COMMISSION,
  LINE_ITEM_VOUCHER,
  LINE_ITEM_WALLET_CREDIT,
} from '../../util/types';

import css from './OrderBreakdown.module.css';

const { Money } = sdkTypes;

/**
 * Calculates the total price in sub units for multiple line items.
 */
const lineItemsTotal = (lineItems, marketplaceCurrency) => {
  const amount = lineItems.reduce((total, item) => {
    return total.plus(item.lineTotal.amount);
  }, new Decimal(0));
  const currency = lineItems[0] ? lineItems[0].lineTotal.currency : marketplaceCurrency;
  // CRITICAL FIX: Convert Decimal to number for Money constructor
  return new Money(amount.toNumber(), currency);
};

/**
 * Checks if line item represents commission
 */
const isCommission = lineItem => {
  return (
    lineItem.code === LINE_ITEM_PROVIDER_COMMISSION ||
    lineItem.code === LINE_ITEM_CUSTOMER_COMMISSION
  );
};

/**
 * Checks if line item represents a discount that should be excluded from subtotal
 * Note: Session promos and seasonal adjustments ARE included in subtotal
 * Only voucher codes and wallet credits are excluded
 */
const isDiscount = lineItem => {
  return (
    lineItem.code === LINE_ITEM_VOUCHER ||
    lineItem.code === LINE_ITEM_WALLET_CREDIT
  );
};

/**
 * Returns non-commission, non-reversal, non-discount line items
 * Subtotal should be the sum of base charges before any discounts
 */
const nonCommissionNonReversalLineItems = lineItems => {
  return lineItems.filter(item => !isCommission(item) && !item.reversal && !isDiscount(item));
};

/**
 * Check if there is a commission line-item for the given user role.
 */
const hasCommission = (lineItems, userRole) => {
  let commissionLineItem = null;

  if (userRole === 'customer') {
    commissionLineItem = lineItems.find(item => item.code === LINE_ITEM_CUSTOMER_COMMISSION);
  } else if (userRole === 'provider') {
    commissionLineItem = lineItems.find(item => item.code === LINE_ITEM_PROVIDER_COMMISSION);
  }
  return !!commissionLineItem;
};

const LineItemSubTotalMaybe = props => {
  const { lineItems, unitType, userRole, intl, marketplaceCurrency } = props;

  const refund = lineItems.find(item => item.code === unitType && item.reversal);

  // Show subtotal only if commission line-item is applicable to user or refund is issued.
  const showSubTotal = hasCommission(lineItems, userRole) || refund;

  // all non-reversal, non-commission line items
  const subTotalLineItems = nonCommissionNonReversalLineItems(lineItems);
  // line totals of those line items combined
  let subTotal = lineItemsTotal(subTotalLineItems, marketplaceCurrency);

  // DEBUG: Log subtotal calculation
  if (process.env.NODE_ENV === 'development') {
      // console.log('[LineItemSubTotalMaybe] Calculation:', {
      //   subTotalLineItems: subTotalLineItems.map(item => ({ code: item.code, amount: item.lineTotal.amount })),
      //   basSubTotal: subTotal.amount,
      //   userRole
      // });
  }

  // For customers: add customer fee to subtotal (they see inclusive prices)
  // For providers: subtotal is base only
  if (userRole === 'customer') {
    const customerCommissionLineItem = lineItems.find(
      item => item.code === LINE_ITEM_CUSTOMER_COMMISSION && !item.reversal
    );
    if (customerCommissionLineItem) {
      const totalAmount = subTotal.amount + customerCommissionLineItem.lineTotal.amount;
      subTotal = new Money(totalAmount, subTotal.currency);
      
      if (process.env.NODE_ENV === 'development') {
          // console.log('[LineItemSubTotalMaybe] Added customer commission:', {
          //   commissionAmount: customerCommissionLineItem.lineTotal.amount,
          //   finalSubTotal: subTotal.amount
          // });
      }
    }
  }

  const formattedSubTotal = subTotalLineItems.length > 0 ? formatMoney(intl, subTotal, true) : null;  return formattedSubTotal && showSubTotal ? (
    <>
      <hr className={css.divider} />
      <div className={css.subTotalLineItem}>
        <span className={css.itemLabel}>
          <FormattedMessage id="BookingBreakdown.subTotal" />
        </span>
        <span className={css.itemValue}>{formattedSubTotal}</span>
      </div>
    </>
  ) : null;
};

LineItemSubTotalMaybe.propTypes = {
  lineItems: propTypes.lineItems.isRequired,
  userRole: string.isRequired,
  intl: intlShape.isRequired,
};

export default LineItemSubTotalMaybe;
